import { App, Button, Form, Input, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import * as authApi from '@/api/auth'
import { useRequest } from 'ahooks';
import { useTranslation } from 'react-i18next';

interface ResetPasswordProps {
    open: boolean;
    dialog?: boolean;
    onClose: () => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ open, dialog = true, onClose }) => {
    const { message } = App.useApp();
    const [form] = Form.useForm<{
        email: string;
        verificationCode: string;
        password: string;
        confirmPassword: string;
    }>();
    const { t } = useTranslation('common');

    const { loading, run } = useRequest(authApi.resetPassword, {
        manual: true,
        onError: (error) => {
            console.error('Error resetting password:', error);
            message.error(t('reset_password.failed'));
        },
        onSuccess: (data) => {
            if (!data?.ok) {
                message.error(data?.message || t('reset_password.failed'));
            } else {
                message.success(t('reset_password.success'));
                form.resetFields();
                onClose();
            }
        }
    })

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await run({
                password: values.password,
                confirmPassword: values.confirmPassword,
                email: values.email,
                verificationCode: values.verificationCode
            });
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const { loading: sendVerificationLoading, run: runSendVerification } = useRequest(async () => {
        return await authApi.sendVerificationCode(form.getFieldValue('email'), 'resetPassword');
    }, {
        manual: true,
        onError: (error) => {
            console.error('Error sending verification code:', error);
            message.error(t('reset_password.send_code_failed'));
        },
        onSuccess: (data) => {
            if (!data?.ok) {
                message.error(data?.message || t('reset_password.send_code_failed'));
            } else {
                message.success(t('reset_password.send_code_success'));
                setCountdown(60);
            }
        },
    })

    const formContent = (
        <Form
            form={form}
            layout="vertical"
            className="mt-4"
            autoComplete="off"
        >
            <Form.Item
                label={t('reset_password.email')}
                name="email"
                rules={[
                    { required: true, message: t('reset_password.email_required') },
                    { type: 'email', message: t('reset_password.email_invalid') }
                ]}
            >
                <Input autoComplete="false" className="w-full" placeholder={t('reset_password.email_placeholder')} />
            </Form.Item>
            <Form.Item
                label={t('reset_password.verification_code')}
                name="verificationCode"
                rules={[
                    { required: true, message: t('reset_password.code_required') },
                    { len: 6, message: t('reset_password.code_length') }
                ]}
            >
                <div className="flex gap-2">
                    <Input
                        autoComplete="off"
                        className="flex-1"
                        placeholder={t('reset_password.code_placeholder')}
                        maxLength={6}
                    />
                    <Button
                        type="primary"
                        loading={sendVerificationLoading}
                        disabled={countdown > 0}
                        onClick={runSendVerification}
                        className="whitespace-nowrap"
                    >
                        {countdown > 0 ? `${countdown}s` : t('reset_password.get_code')}
                    </Button>
                </div>
            </Form.Item>
            <Form.Item
                label={t('reset_password.new_password')}
                name="password"
                rules={[
                    {
                        required: true,
                        validator: async (_rule, value) => {
                            if (!value || value.length < 8 || value.length > 20) {
                                return Promise.reject(t('reset_password.password_length'));
                            }

                            if (value && !/[A-Z]/.test(value)) {
                                return Promise.reject(t('reset_password.password_uppercase'));
                            }
                            if (value && !/[a-z]/.test(value)) {
                                return Promise.reject(t('reset_password.password_lowercase'));
                            }
                            if (value && !/[0-9]/.test(value)) {
                                return Promise.reject(t('reset_password.password_digit'));
                            }

                            const res = await authApi.verifyPasswordStrength({ password: value });
                            if (!res?.ok) {
                                return Promise.reject(res?.message || t('reset_password.password_weak'));
                            }
                        },
                    }
                ]}
            >
                <Input.Password autoComplete="off" className="w-full" />
            </Form.Item>

            <Form.Item
                label={t('reset_password.confirm_password')}
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                    { required: true, message: t('reset_password.confirm_required') },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error(t('reset_password.confirm_mismatch')));
                        },
                    }),
                ]}
            >
                <Input.Password autoComplete="off" className="w-full" />
            </Form.Item>

            {!dialog && (
                <Form.Item>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        className="w-full"
                    >
                        {t('reset_password.submit')}
                    </Button>
                </Form.Item>
            )}
        </Form>
    );

    if (dialog) {
        return (
            <Modal
                title={t('reset_password.title')}
                open={open}
                onCancel={onClose}
                onOk={handleSubmit}
                loading={loading}
                className="w-[400px]"
            >
                {formContent}
            </Modal>
        );
    }

    // Embedded mode - only show when open is true
    if (!open) return null;

    return (
        <div className="w-full max-w-md">
            {formContent}
        </div>
    );
};

export default ResetPassword;